import ResponsiveContainer from "../../../layouts/ResponsiveContainer"
import atencion from '../../../assets/img/atencion.png'
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import axiosInstance from "../../../api/axiosInstance";
import { FormInput } from "../../../components/Inputs/Text/FormInput";
import Button from "../../../components/Buttons/Button";
import { AiOutlineSend } from "react-icons/ai";
import { MdCheck } from "react-icons/md";
import { FormMultiSelect } from "../../../components/Inputs/Selects/FormMultiSelect";
import { IOptions } from "../../../interfaces/Rights";
const { REACT_APP_API_PATH } = process.env;

type TSubscribeForm = {
    nombre_apellido: string;
    correo: string;
    organizacion: string;
    tipo_perfil_ids: string[]
}

interface ISelectedItem {
    value: string;
    label: string;
}

const Subscription = ({ profiles }: { profiles: IOptions[] }) => {
    const { register, handleSubmit, reset, setValue, setError, watch, clearErrors, formState: { errors, isValid, isDirty } } = useForm<TSubscribeForm>({ mode: 'onChange' });

    const formRef = useRef<HTMLFormElement>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<ISelectedItem[]>([]);
    const [isLockedByDuplicate, setIsLockedByDuplicate] = useState(false);
    const [duplicateEmail, setDuplicateEmail] = useState<string | null>(null);

    const correoValue = watch('correo');

    useEffect(() => {
        if (errors.correo?.type === 'manual') {
            setDuplicateEmail(correoValue);
            setIsLockedByDuplicate(true);
        }
    }, [errors.correo]);

    useEffect(() => {
        if (isLockedByDuplicate && duplicateEmail && correoValue !== duplicateEmail) {
            setIsLockedByDuplicate(false);
            setDuplicateEmail(null);
            setIsSubmitSuccessful(false);
            clearErrors('correo');
        }
    }, [correoValue, isLockedByDuplicate, duplicateEmail]);

    const onSubmit = async (data: TSubscribeForm) => {
        const selectedIds = selectedItems
            .map(item => {
                const profile = profiles.find(p => p.value === item.value);
                return profile ? profile.id.toString() : null;
            })
            .filter(id => id !== null) as string[];

        const payload = {
            ...data,
            tipo_perfil_ids: selectedIds,
            category: 'Por front'
        };

        setLoading(true);
        try {
            const response = await axiosInstance.post(`${REACT_APP_API_PATH}/public/subscriptions`, payload);

            if (response.data) {
                if (response.data.data.alreadySubscribed === true) {
                    setError('correo', {
                        type: 'manual',
                        message: 'Este correo ya está suscripto'
                    });
                    setLoading(false);
                    setIsSubmitSuccessful(false);
                    return;
                } else {
                    reset({ nombre_apellido: "", correo: "", organizacion: "", tipo_perfil_ids: [] });
                    setSelectedItems([]);
                    setIsSubmitSuccessful(true);

                    setTimeout(() => {
                        setIsSubmitSuccessful(false);
                        setDuplicateEmail(null);
                    }, 5000);
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (item: ISelectedItem) => {
        const alreadySelected = selectedItems.some(i => i.value === item.value);

        let updatedItems: ISelectedItem[];

        if (alreadySelected) {
            updatedItems = selectedItems.filter(i => i.value !== item.value);
        } else {
            updatedItems = [...selectedItems, item];
        }

        setSelectedItems(updatedItems);
        setValue(
            'tipo_perfil_ids',
            updatedItems.map(i => i.value),
            { shouldValidate: true }
        );
    };

    return (
        <section className="w-full flex justify-center py-12 sm:py-13 bg-brand-green-30" id="subscription">
            <ResponsiveContainer className="md:border md:border-brand-green-10 sm:rounded-sm h-full lg:h-[19.56rem]">
                <div className="md:py-[3.25rem] md:px-20 flex flex-col lg:flex-row gap-6 xl:gap-12 justify-center items-end relative ">
                    <div className="w-full lg:w-4/5 xl:w-1/2 flex flex-col items-center lg:items-start">
                        <img src={atencion} alt="Atención" className="w-[5.4375rem] h-[4.5rem] xl:w-[6.125rem] xl:h-[5.06rem]" />
                        <h3 className="text-hsmall text-center md:text-left md:text-hmedium text-primary-100 my-4">Recibí nuestros informes y novedades.</h3>
                    </div>
                    <form ref={formRef} onSubmit={handleSubmit(onSubmit)} className='flex flex-col items-center justify-center w-full gap-1'>
                        <div className='md:flex md:flex-row gap-4 w-full '>
                            <FormInput
                                id="correo"
                                name="correo"
                                type="email"
                                label="Correo"
                                register={register}
                                rules={{
                                    required: 'El correo es obligatorio',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'Formato de correo inválido'
                                    }
                                }}
                                errors={errors}
                                className="w-full md:w-1/2 h-[5rem]"
                                classNameInput={`bg-transparent h-[2.5rem] text-primary-100 ${errors.correo ? 'border-[#BA1A1A]' : '!border-brand-green-10'}`}
                                classNameLabel="text-primary-100"
                                isRequired={true}
                            />
                            <FormInput
                                id='nombre_apellido'
                                name='nombre_apellido'
                                label='Nombre y apellido'
                                type='text'
                                placeholder=''
                                register={register}
                                className="w-full md:w-1/2 h-[5rem]"
                                classNameInput="bg-transparent !border-brand-green-10 h-[2.5rem]  text-primary-100"
                                classNameLabel="text-primary-100"
                            />
                        </div>
                        <div className='md:flex md:flex-row justify-between gap-4 w-full'>
                            <FormInput
                                id='organizacion'
                                name='organizacion'
                                label='Organización'
                                type='text'
                                register={register}
                                className="w-full md:w-1/2 h-[5rem]"
                                classNameInput="bg-transparent !border-brand-green-10 h-[2.5rem]  text-primary-100"
                                classNameLabel="text-primary-100"
                            />
                            <FormMultiSelect
                                name="tipo_perfil_ids"
                                label="Tipo de perfil"
                                options={profiles}
                                selectedItems={selectedItems}
                                onSelect={handleSelect}
                                rules={{
                                    validate: (value) =>
                                        (value && value.length > 0) || 'Debe seleccionar al menos un perfil',
                                }}
                                errors={errors}
                                className="w-full md:w-1/2"
                                classNameInput="!h-[2.5rem]"
                                isRequired={true}
                                variant='outline-teal'
                            /> 
                        </div>
                        <Button
                            label={isSubmitSuccessful ? 'Suscripto' : 'Suscribite'}
                            type='submit'
                            showIconLeft={false}
                            showIconRight={false}
                            className={`!w-full justify-center mt-6 md:mt-0 ${isSubmitSuccessful && !loading ? '!opacity-50' : ''}`}
                            variant="primary"
                            disabled={isLockedByDuplicate || !selectedItems.length || !isDirty || !isValid || loading}
                        />
                    </form>
                </div>
            </ResponsiveContainer>
        </section>
    )
}

export default Subscription